import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Terminal, Database, Layout, Sparkles } from "lucide-react";

const About = () => {
  return (
    <section 
      id="about" 
      className="relative min-h-[90vh] flex items-center overflow-hidden bg-[#FAFAFA] dark:bg-[#09090B] pt-20 sm:pt-32 pb-16"
    >
      {/* Subtle Ambient Background Glow */}
      <div className="absolute top-0 right-0 -translate-y-12 translate-x-1/3 pointer-events-none">
        <div className="w-[500px] h-[500px] bg-emerald-500/5 dark:bg-emerald-500/10 rounded-full blur-[120px]"></div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10 w-full">
        <div className="flex flex-col lg:flex-row items-center gap-16 lg:gap-24">
          
          {/* Left Column: Typography & Intent */}
          <div className="w-full lg:w-1/2 space-y-10">
            <motion.div 
              initial={{ opacity: 0, y: 20 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            >
              {/* Premium Availability Badge */}
              <div className="inline-flex items-center px-3 py-1.5 rounded-full bg-zinc-100 dark:bg-white/5 border border-zinc-200 dark:border-white/10 mb-8">
                <span className="relative flex h-2 w-2 mr-2.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                </span>
                <span className="text-xs font-medium text-zinc-600 dark:text-zinc-300 tracking-wide uppercase">
                  Available for New Opportunities
                </span>
              </div>

              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-display font-semibold tracking-tight text-zinc-900 dark:text-white leading-[1.1]">
                Gebre <span className="text-zinc-400 dark:text-zinc-500">Wagnew.</span>
              </h1>
              
              <h2 className="text-2xl sm:text-3xl lg:text-4xl mt-4 font-medium text-zinc-700 dark:text-zinc-300 tracking-tight">
                Software Engineer & <br className="hidden sm:block"/>
                Digital Product Architect.
              </h2>
            </motion.div>

            <motion.p 
              initial={{ opacity: 0, y: 20 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ duration: 0.5, delay: 0.1, ease: [0.22, 1, 0.36, 1] }} 
              className="text-lg text-zinc-600 dark:text-zinc-400 leading-relaxed max-w-xl"
            >
              I specialize in building high-performance web applications using the MERN stack and Next.js. With a foundation from Adama Science and Technology University, I architect scalable solutions—from real-time chat systems to robust inventory platforms.
            </motion.p>

            <motion.div 
              initial={{ opacity: 0, y: 20 }} 
              animate={{ opacity: 1, y: 0 }} 
              transition={{ duration: 0.5, delay: 0.2, ease: [0.22, 1, 0.36, 1] }} 
              className="flex flex-wrap items-center gap-4"
            >
              <a 
                href="#projects" 
                className="inline-flex items-center justify-center px-8 py-3.5 rounded-full text-sm font-medium transition-all duration-300 bg-zinc-900 text-white hover:bg-zinc-800 dark:bg-zinc-100 dark:text-zinc-900 dark:hover:bg-white hover:scale-105 active:scale-95 shadow-lg shadow-zinc-900/20 dark:shadow-white/10"
              >
                View Case Studies
              </a>
              <a 
                href="#contact" 
                className="group inline-flex items-center justify-center px-8 py-3.5 rounded-full text-sm font-medium transition-all duration-300 bg-zinc-100 text-zinc-900 hover:bg-zinc-200 dark:bg-white/5 dark:text-white dark:hover:bg-white/10 border border-zinc-200 dark:border-white/10"
              >
                Let's Talk
                <ArrowRight className="w-4 h-4 ml-2 transition-transform duration-300 group-hover:translate-x-1" />
              </a>
            </motion.div>
          </div>

          {/* Right Column: Code-Native Architecture Abstract */}
          <motion.div 
            initial={{ opacity: 0, scale: 0.95 }} 
            animate={{ opacity: 1, scale: 1 }} 
            transition={{ duration: 0.7, delay: 0.3, ease: [0.22, 1, 0.36, 1] }} 
            className="w-full lg:w-1/2 relative perspective-1000"
          >
            <div className="relative w-full max-w-lg mx-auto aspect-square">
              
              {/* Central Node (Backend/API) */}
              <motion.div 
                animate={{ y: [0, -10, 0] }} 
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 h-48 rounded-full bg-emerald-500/10 dark:bg-emerald-500/20 blur-2xl pointer-events-none"
              />
              
              {/* Layer 1: Frontend (Next.js/React) */}
              <motion.div 
                animate={{ y: [0, -8, 0] }} 
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                className="absolute top-[10%] left-[5%] w-[60%] p-5 rounded-2xl bg-white/80 dark:bg-zinc-900/80 backdrop-blur-xl border border-zinc-200 dark:border-white/10 shadow-2xl z-20"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Layout className="w-4 h-4 text-emerald-500" />
                    <span className="text-xs font-mono font-medium text-zinc-500 dark:text-zinc-400">Client Interface</span>
                  </div>
                  <div className="flex gap-1">
                    <div className="w-2 h-2 rounded-full bg-zinc-300 dark:bg-zinc-700"></div>
                    <div className="w-2 h-2 rounded-full bg-zinc-300 dark:bg-zinc-700"></div>
                  </div>
                </div>
                <div className="space-y-2">
                  <div className="h-2 w-full rounded bg-zinc-200 dark:bg-zinc-800"></div>
                  <div className="h-2 w-4/5 rounded bg-zinc-200 dark:bg-zinc-800"></div>
                  <div className="h-2 w-full rounded bg-zinc-200 dark:bg-zinc-800"></div>
                </div>
              </motion.div>

              {/* Layer 2: Server (Node.js/Nest) */}
              <motion.div 
                animate={{ y: [0, 8, 0] }} 
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                className="absolute top-[40%] right-[5%] w-[55%] p-5 rounded-2xl bg-white/90 dark:bg-zinc-900/90 backdrop-blur-xl border border-zinc-200 dark:border-white/10 shadow-2xl z-10"
              >
                 <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Terminal className="w-4 h-4 text-emerald-500" />
                    <span className="text-xs font-mono font-medium text-zinc-500 dark:text-zinc-400">API Gateway</span>
                  </div>
                </div>
                <div className="font-mono text-[10px] text-zinc-600 dark:text-zinc-400 space-y-1">
                  <p><span className="text-emerald-500">GET</span> /api/v1/metrics</p>
                  <p className="pl-4 text-zinc-400 dark:text-zinc-500">status: 200 OK</p>
                  <p><span className="text-emerald-500">POST</span> /api/v1/sync</p>
                  <p className="pl-4 text-zinc-400 dark:text-zinc-500">streaming...</p>
                </div>
              </motion.div>

              {/* Layer 3: Database (Mongo/Postgres) */}
              <motion.div 
                animate={{ y: [0, -5, 0] }} 
                transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut", delay: 2 }}
                className="absolute bottom-[10%] left-[15%] w-[50%] p-5 rounded-2xl bg-zinc-50 dark:bg-zinc-950 border border-zinc-200 dark:border-white/5 shadow-xl z-0"
              >
                 <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Database className="w-4 h-4 text-zinc-400" />
                    <span className="text-xs font-mono font-medium text-zinc-500">Data Store</span>
                  </div>
                </div>
                <div className="flex gap-2">
                  <div className="w-8 h-8 rounded-full border-2 border-zinc-200 dark:border-zinc-800 border-t-emerald-500 animate-spin"></div>
                  <div className="flex-1 space-y-1.5 py-1">
                    <div className="h-1.5 w-full rounded bg-zinc-200 dark:bg-zinc-800"></div>
                    <div className="h-1.5 w-2/3 rounded bg-zinc-200 dark:bg-zinc-800"></div>
                  </div>
                </div>
              </motion.div>

            </div>
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default About;